import React, { useEffect, useMemo, useState } from 'react';

import { Web } from '@mui/icons-material';
import { Row, Form } from 'react-bootstrap';
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';

import RiskClaimsValidationTable, { riskClaimsValidationColumns } from './RiskClaimsValidationTable';
import Excel from '../../../assets/excel.png';
import { Breadcrumbs, Button, EdgeReportingWindow, SubHeader, ReportLoader } from '../../../components';
import { newDateFormatted } from '../../../utils';
import ImageColorChanger from '../../../utils/imageColorChanger';
import { formatPhoneNumber } from '../../../utils/utilities';
import { getStates } from '../../DriverQualification/ListDQ/NewRequest/operations';
import getCSVData from '../csv';
import getEdgeReportingMenu from '../getEdgeReportingMenu';
import { generateReport, getReportData, removeReportData, userHasAccess } from '../operations';
import { customStyles, formatSSNO } from '../utils';

import './styles.scoped.css';

const RiskClaimsValidationForm = ({ setForm, form, stateOptions, setLoadingResults, setData, setShowResults }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useSelector(state => state.admin);
  const handleChange = e => {
    const _form = {
      ...form,
      [e.target.name]: e.target.name === 'EmplPhone' ? formatPhoneNumber(e.target.value) : e.target.value,
    };
    setForm(_form);
  };

  const viewFullPii = useMemo(
    () => user?.appAccess?.items?.find(item => item.appId.id === '4')?.roleId?.viewFullPii,
    [user],
  );

  const handleViewResults = async () => {
    setLoadingResults(true);
    const {
      EmplLastName = '',
      EmplFirstName = '',
      SSNO = '',
      State = '',
      City = '',
      ZipCode = '',
      EmplPhone = '',
      EmpId = 0,
    } = form;
    try {
      const response = await generateReport({
        endpoint: 'risk-claims-validation-report',
        payload: {
          EmplLastName,
          EmplFirstName,
          SSNO,
          State,
          City,
          ZipCode,
          EmplPhone,
          EmpId,
          viewFullPii,
        },
      });
      if (response.location && response.location !== '') {
        const reportData = await getReportData(response.location);
        if (reportData?.length) {
          await removeReportData(response.location);
          setLoadingResults(false);
          setData(reportData);
          setShowResults(true);
        }
      } else {
        setLoadingResults(false);
        setLoadingResults(false);
        // eslint-disable-next-line no-undef
        alert('No data found matching your criteria.');
      }
    } catch (error) {
      console.log(error);
      setLoadingResults(false);
    }
  };

  return (
    <Form
      noValidate
      onSubmit={() => {}}
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        paddingTop: 20,
      }}>
      <div className="filters-row">
        <div className="filter-row">
          <Form.Group as={Row} controlId="EmplLastName" style={{ alignItems: 'center' }}>
            <Form.Label style={{ width: '290px' }}>
              {t('edge_reporting.risk_claims_validation.driver')} Last Name
            </Form.Label>
            <Form.Control
              style={{
                width: '360px',
                height: '30px',
                marginRight: '20px',
              }}
              as="input"
              name="EmplLastName"
              defaultValue={form?.EmplLastName}
              onChange={handleChange}
            />
          </Form.Group>
        </div>

        <div className="filter-row">
          <Form.Group as={Row} controlId="EmplFirstName" style={{ alignItems: 'center' }}>
            <Form.Label style={{ width: '290px' }}>
              {t('edge_reporting.risk_claims_validation.driver')} First Name
            </Form.Label>
            <Form.Control
              style={{
                width: '360px',
                height: '30px',
                marginRight: '20px',
              }}
              as="input"
              name="EmplFirstName"
              defaultValue={form?.EmplFirstName}
              onChange={handleChange}
            />
          </Form.Group>
        </div>

        <div className="filter-row">
          <Form.Group as={Row} style={{ alignItems: 'center' }}>
            <Form.Label style={{ width: '290px' }}>
              {t('edge_reporting.risk_claims_validation.driver')} Number
            </Form.Label>
            <Form.Control
              style={{
                width: '360px',
                height: '30px',
                marginRight: '20px',
              }}
              as="input"
              name="EmpId"
              value={form?.EmpId}
              onChange={handleChange}
            />
          </Form.Group>
        </div>

        <div className="filter-row">
          <Form.Group as={Row} controlId="SSNO" style={{ alignItems: 'center' }}>
            <Form.Label style={{ width: '290px' }}>{t('edge_reporting.risk_claims_validation.driver')} SSN</Form.Label>
            <Form.Control
              style={{
                width: '360px',
                height: '30px',
                marginRight: '20px',
              }}
              as="input"
              name="SSNO"
              value={formatSSNO(form?.SSNO)}
              onChange={handleChange}
            />
          </Form.Group>
        </div>
        <div className="filter-row">
          <Form.Group as={Row} controlId="locationState">
            <Form.Label style={{ width: 290 }}>State</Form.Label>
            <Form.Group>
              <div className="dropdown-spinner-container">
                <Select
                  isClearable
                  styles={customStyles('360px')}
                  options={stateOptions}
                  onChange={e => {
                    const _form = { ...form, State: e?.value || '' };
                    setForm(_form);
                  }}
                  value={stateOptions?.filter(state => state.value === form.State)}
                />
              </div>
            </Form.Group>
          </Form.Group>
        </div>
        <div className="filter-row">
          <Form.Group as={Row} controlId="locationState">
            <Form.Label style={{ width: 290 }}>City</Form.Label>
            <Form.Control
              style={{
                width: '360px',
                height: '30px',
              }}
              as="input"
              name="City"
              value={form?.City}
              onChange={handleChange}
            />
          </Form.Group>
        </div>
        <div className="filter-row">
          <Form.Group as={Row} controlId="EmplFirstName" style={{ alignItems: 'center' }}>
            <Form.Label style={{ width: '290px' }}>Zip/Postal Code</Form.Label>
            <Form.Control
              style={{
                width: '360px',
                height: '30px',
                marginRight: '20px',
              }}
              as="input"
              name="ZipCode"
              defaultValue={form?.ZipCode}
              onChange={handleChange}
            />
          </Form.Group>
        </div>
        <div className="filter-row">
          <Form.Group as={Row} controlId="EmplFirstName" style={{ alignItems: 'center' }}>
            <Form.Label style={{ width: '290px' }}>
              {t('edge_reporting.risk_claims_validation.driver')} Phone
            </Form.Label>
            <Form.Control
              style={{
                width: '360px',
                height: '30px',
                marginRight: '20px',
              }}
              as="input"
              name="EmplPhone"
              value={form?.EmplPhone}
              onChange={handleChange}
            />
          </Form.Group>
        </div>
      </div>
      <div className="buttons-row">
        <Button variant="primary" onPress={() => handleViewResults()} style={{ marginRight: '20px', width: '100px' }} text="View" />

        <Button
          style={{ width: '100px' }}
          onPress={() => navigate('/edge-reporting')}
          variant="outline"
          text="Cancel"
        />
      </div>
    </Form>
  );
};

const RiskClaimsValidation = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { menuItems } = getEdgeReportingMenu(t);
  const { user } = useSelector(state => state.admin);
  const [data, setData] = useState([]);
  const [form, setForm] = useState({});
  const [loadingResults, setLoadingResults] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [locationData, setLocationData] = useState();

  useEffect(() => {
    if (menuItems) {
      // eslint-disable-next-line no-undef
      if (!userHasAccess(user, menuItems.find(i => i.to === window.location.pathname)?.id)) navigate('/');
    }
  }, [menuItems]);

  useEffect(() => {
    getAndSetFormData();
  }, []);

  const getAndSetFormData = async () => {
    const states = await getStates();
    if (states) {
      setLocationData({ ...locationData, states: states });
    }
  };

  const stateOptions = locationData?.states
    ?.sort((a, b) => (a.name > b.name ? 1 : -1))
    .map(({ name, id }) => ({
      label: name,
      value: id,
    }));

  const fileName = `edge-reporting-risk-claims-validation-${newDateFormatted.toString()}.csv`;

  return (
    <>
      <SubHeader
        text="EDGE Reporting"
        buttons={[{ icon: <Web sx={{ color: '#fff', fontSize: 30 }} />, onPress: () => navigate('/dashboard') }]}
        popoverTitle="Need help with your Risk Claims Validation report?"
      />
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '30px',
        }}>
        <Breadcrumbs
          leftTitle="Reporting Home"
          middleTitle={showResults && 'Risk Claims Validation'}
          midItemAction={() => setShowResults(false)}
          rightTitle={showResults ? 'Viewing Report' : 'Risk Claims Validation'}
          route="/edge-reporting"
        />
        {!!data?.length && showResults && (
          <CSVLink filename={fileName} data={getCSVData(data, riskClaimsValidationColumns)}>
            <ImageColorChanger
              src={Excel}
              style={{ padding: '20px 30px 0 0 ', cursor: 'pointer' }}
              alt="export-to-excel"
            />
          </CSVLink>
        )}
      </div>
      {loadingResults && <ReportLoader didAbort={() => setLoadingResults(false)} />}
      {showResults ? (
        <>
          <RiskClaimsValidationTable data={data} loading={loadingResults} />
          <div className="buttons-row">
            <Button variant="primary" onClick={() => setShowResults(false)} style={{ marginRight: '20px', width: '100px' }} text="Back" />

            <Button
              style={{ width: '100px' }}
              onClick={() => navigate('/edge-reporting')}
              variant="outline"
              text="Cancel"
            />
          </div>
        </>
      ) : (
        <EdgeReportingWindow
          containerStyle={{
            border: '2px solid #343a40',
          }}
          windowContent={
            <RiskClaimsValidationForm
              form={form}
              setForm={setForm}
              stateOptions={stateOptions}
              setLoadingResults={setLoadingResults}
              setData={setData}
              setShowResults={setShowResults}
            />
          }
          windowTitle="Criteria"
          headerStyle={{ backgroundColor: '#343a40' }}
        />
      )}
    </>
  );
};

export default RiskClaimsValidation;
